import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useToast } from '@ntconsult/aluma-react';
import { fileService } from '../services/fileService';

interface UploadOptions {
  folderId: string;
  onSuccess?: () => void;
}

interface UploadingFile {
  id: string;
  name: string;
  progress: number;
  status: 'uploading' | 'processing' | 'done' | 'error';
}

export interface UploadMetadata {
  title: string;
  description?: string;
  tags?: string[];
}

const extractVideoDuration = (file: globalThis.File): Promise<number | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) {
      resolve(null);
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(Math.round(video.duration));
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve(null);
    };

    video.src = URL.createObjectURL(file);
  });
};

export function useUpload({ folderId, onSuccess }: UploadOptions) {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('files');
  const cleanupTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timeouts = cleanupTimeouts.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const uploadFile = useCallback(
    async (file: globalThis.File, metadata?: UploadMetadata) => {
      const tempId = crypto.randomUUID();

      setUploadingFiles((prev) => [
        ...prev,
        { id: tempId, name: metadata?.title ?? file.name, progress: 0, status: 'uploading' },
      ]);
      setPendingCount((c) => c + 1);

      try {
        // 1. Get pre-signed URL
        const { uploadUrl, fileId } = await fileService.getUploadUrl({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          folderId,
        });

        // 2. Upload to S3
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadingFiles((prev) =>
              prev.map((f) => (f.id === tempId ? { ...f, progress: percent } : f))
            );
          },
        });

        // 3. Mark as processing
        setUploadingFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: 'processing', progress: 100 } : f))
        );

        // 4. Extract video duration
        const duration = await extractVideoDuration(file);

        // 5. Confirm upload with duration
        await fileService.confirmUpload(fileId, duration ?? undefined);

        // 6. Apply metadata (title, description, tags)
        if (metadata) {
          await fileService.updateFile(fileId, {
            name: metadata.title,
            description: metadata.description,
            tags: metadata.tags,
          });
        }

        // 7. Done
        setUploadingFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: 'done' } : f))
        );

        // 8. Invalidate cache
        queryClient.invalidateQueries({ queryKey: ['files'] });
        queryClient.invalidateQueries({ queryKey: ['folders'] });

        toast.success({
          title: t('toasts.uploadSuccess', { name: metadata?.title ?? file.name }),
        });
        onSuccess?.();

        // Remove from list after delay (timeout cancelado no unmount)
        const timeout = setTimeout(() => {
          cleanupTimeouts.current.delete(timeout);
          setUploadingFiles((prev) => prev.filter((f) => f.id !== tempId));
        }, 2000);
        cleanupTimeouts.current.add(timeout);
      } catch {
        setUploadingFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: 'error' } : f))
        );
        toast.danger({
          title: t('toasts.uploadError', { name: metadata?.title ?? file.name }),
        });
      } finally {
        setPendingCount((c) => c - 1);
      }
    },
    [folderId, queryClient, onSuccess, toast, t]
  );

  return {
    uploadFile,
    uploadingFiles,
    isUploading: pendingCount > 0,
  };
}
